import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Button } from '../../..'
import BarChart from '../../../../charts/bar-chart'
import * as serializers from '../../../../charts/chart_serializer.js'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { useTrackingContext } from '../../../../providers/tracking'
import { useAppSelector } from '../../../../store/hooks'
import { selectPropertyDetailsMap } from '../../../../store/selectors/airDna'
import { IconLock } from '../../Icon'
import styles from './AirDnaChart.module.scss'

const TABS = [
  {
    key: 'monthlyRevenue',
    render: <FormattedMessage id='AirDna.chartTab.monthlyRevenue' defaultMessage='Monthly Revenue' />
  },
  {
    key: 'nightlyRate',
    lockable: true,
    render: <FormattedMessage id='AirDna.chartTab.nightlyRate' defaultMessage='Nightly Rate' />
  },
  {
    key: 'monthlyOccupancy',
    lockable: true,
    render: <FormattedMessage id='AirDna.chartTab.monthlyOccupancy' defaultMessage='# of nights' />
  }
]

const getTrackingLocation = key => {
  switch (key) {
    case 'monthlyRevenue':
      return TrackingEventConstants.MONTHLY_REVENUE
    case 'nightlyRate':
      return TrackingEventConstants.NIGHTLY_RATE
    case 'monthlyOccupancy':
      return TrackingEventConstants.NUMBER_OF_NIGHTS
    default:
      return null
  }
}

export interface AirDnaChartProps {
  locked: boolean
  stateKey: string
}

export const AirDnaChart = ({ locked, stateKey }: AirDnaChartProps) => {
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()
  const [selectedTab, setSelectedTab] = useState('monthlyRevenue')
  const locale = useAppSelector(state => state.locale.locale)
  const airDnaData = useAppSelector(state => state.airDna[stateKey].data)
  const { monthlyRevenue, monthlyEarnings, annualEarnings } = useSelector(selectPropertyDetailsMap)[stateKey] || {}
  const percentages = {
    monthlyRevenue,
    monthlyEarnings,
    annualEarnings
  }

  const handleTabChange = key => {
    trackingClient?.airbnbExplored({
      airbnb_clicked: getTrackingLocation(key)
    })

    setSelectedTab(key)
  }

  useEffect(() => {
    const chartSvg = document.getElementById('air-dna-chart-svg')
    const percentage = percentages[selectedTab] || 1
    const serialize = serializers[selectedTab]
    let chart: BarChart

    if (chartSvg && airDnaData && percentage && serialize) {
      chart = new BarChart(chartSvg)
      const data = serialize(intl, airDnaData.stats.future.metrics, percentage)
      chart.update(data, locale)
    }

    return () => chart?.destroy()
  }, [percentages, selectedTab, airDnaData, locale])

  return (
    <div className={styles.ChartTableContainer}>
      <ul className={`tabs-v2 ${styles.tabsContainer}`}>
        {TABS.map(({ key, render, lockable }) => (
          <li key={key} className={classnames(styles.tab, selectedTab === key && styles.active)}>
            <Button
              disabled={locked && lockable}
              onClick={() => handleTabChange(key)}
              iconStart={locked && lockable && IconLock}
              children={render}
              className={styles.button}
            />
          </li>
        ))}
      </ul>
      <div id='chart-container' className={`chart-container chart-container--bar-chart ${styles.chart}`}>
        <svg id='air-dna-chart-svg' className='bar-chart' />
      </div>
    </div>
  )
}
